import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ClipboardList, UserPlus } from "lucide-react";
import { RoleStrip, ActionButton, ListRow } from "./kit";
import LoadFailureNote from "./LoadFailureNote";
import { groupByPlayingRole, ROLE_ORDER, ROLE_NOT_SET } from "../lib/playingRoles";
import { fetchPlayerSummaries, aggregateSquadAverage, playerRowStatus, inningsWord } from "../lib/squadAverage";
import { avgText } from "../lib/recordFigures";
import { track } from "../lib/usageTracker";
import { newUi } from "../lib/featureFlags";
import { coachSetupInviteHref } from "../lib/coachSetup";

/**
 * The redrawn, populated coach portal (issue #767, epic #749 wave 6).
 * Behind `newUi("squad")`, on the SAME route and page as today
 * (`/coach-portal`, brief-767.md "Where it lives") — this component
 * replaces only the flat roster card `CoachPortal.jsx` renders in its
 * `lg:col-span-1` slot (see that file's own call site); the selected-
 * player panel beside/below it is untouched (ruling 9). The shared navy
 * `TopBar` still comes from `Layout.jsx`'s `drawnTopBar` mechanism, but
 * THIS component no longer declares it — `CoachPortal.jsx` does, earlier
 * and unconditionally on the flag+role alone (see the F1 note below).
 *
 * Matches `Squad.dc.html` (round 5, sha256 4a39da53…) as ruled by
 * brief-767.md and D168 (`decisions-2026-09-24.md`) — the fourteen S1–S14
 * rulings this component encodes are cited by number in the comments
 * below, not re-argued.
 *
 * `squad-new-screen` is this slice's own per-screen-slice leg marker (the
 * same rule #756's `coach-portal-empty-new-screen` follows) — every
 * testid this component shares with the shipped list (`player-{id}`,
 * `player-record-{id}`, `player-list`, `cp-selected-name`'s deep link) is
 * unchanged by design (ruling 8), so none of them can prove which leg
 * rendered on its own.
 *
 * PR #920 review, F1 — this component no longer opts the route into the
 * shared navy `TopBar` itself. It used to (`useRetireTextSize().setRetired
 * (true)` on mount), but this component only mounts once `showSquadScreen`
 * goes true in `CoachPortal.jsx`, which waits on a real `GET /players`
 * round trip (`rosterLoaded`) — so the drawn TopBar only appeared once
 * THIS component showed up, and everything before that (a real, if
 * usually brief, window) rendered the SHIPPED header instead, with its own
 * differently-propped `NotificationsBell`. `CoachPortal.jsx` now decides
 * "this route wants the drawn chrome" from the flag and the coach's role
 * alone — both known synchronously, before any roster fetch — and owns
 * `setRetired` itself; see that file's own `wantsDrawnCoachChrome` effect.
 */
export default function CoachSquad({ players, selectedId, onSelectPlayer }) {
  const nav = useNavigate();
  const [roleFilter, setRoleFilter] = useState("All");
  const [summaries, setSummaries] = useState(null); // null while loading
  const [reloadKey, setReloadKey] = useState(0);

  const playerIds = (players || []).map((p) => p.player_id);
  // A stable key for the effect below: the same SET of ids in a different
  // order (a re-sort elsewhere) must not re-fetch, but a genuine add/remove
  // (accept, revoke) must.
  const idsKey = [...playerIds].sort().join(",");

  // Ruling 1 — N × GET /stats/summary?player_id=, bounded concurrency (4),
  // never blocking the player list: this effect is independent of the
  // rows below, which render from `players` alone and never wait on it.
  useEffect(() => {
    let cancelled = false;
    setSummaries(null);
    if (playerIds.length === 0) {
      setSummaries({});
      return () => {
        cancelled = true;
      };
    }
    fetchPlayerSummaries(playerIds, { concurrency: 4 }).then((results) => {
      if (cancelled) return;
      const byId = {};
      results.forEach((r) => {
        byId[r.playerId] = r;
      });
      setSummaries(byId);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsKey is the intentional dependency, not playerIds' own identity.
  }, [idsKey, reloadKey]);

  const groups = groupByPlayingRole(players);
  const countOf = (role) => groups.find((g) => g.role === role)?.players.length || 0;
  const total = (players || []).length;
  const roleNotSetCount = countOf(ROLE_NOT_SET);

  const roleOptions = [
    { value: "All", label: `All ${total}`, "data-testid": "squad-role-all" },
    ...ROLE_ORDER.filter((r) => r !== ROLE_NOT_SET).map((r) => ({
      value: r,
      label: `${r} ${countOf(r)}`,
      "data-testid": `squad-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`,
    })),
    // S7/Q-002: the drawing never shows a fifth chip — it never needed to,
    // because the fixture this slice ships maps every player. A real
    // unmapped role must still be reachable rather than silently
    // unfilterable, so the chip appears only when the bucket is non-empty.
    ...(roleNotSetCount > 0
      ? [{ value: ROLE_NOT_SET, label: `${ROLE_NOT_SET} ${roleNotSetCount}`, "data-testid": "squad-role-not-set" }]
      : []),
  ];

  const visibleGroups = groups.filter((g) => (roleFilter === "All" ? true : g.role === roleFilter));

  const average = summaries ? aggregateSquadAverage(playerIds.map((id) => summaries[id])) : null;

  return (
    <div
      data-testid="squad"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nw-space-stack)",
        background: "var(--nw-page)",
        padding: "var(--nw-space-page)",
        // token-check (round 5 conformance) — `color` is inherited, and
        // this root previously set none, so every descendant that also
        // left `color` unset fell through to index.css's raw `#0B1220`
        // reset rather than the declared `--nw-ink` token (#0F172A) —
        // the same fix `SeasonRecordScreen.jsx`'s own root div already
        // carries (`data-testid="season"`).
        color: "var(--nw-ink)",
      }}
    >
      <span data-testid="squad-new-screen" className="sr-only" aria-hidden="true" />

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-eyebrow">Friday night</div>
        <div className="nw-title">Who fills the slots?</div>
        {/* S2 — no club object (#158, parked); the header names only the
            live, real count, never an invented club name. */}
        <div className="nw-prose" style={{ color: "var(--nw-ink-muted)" }}>
          {total} {total === 1 ? "player" : "players"} linked to you
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* S5 — wraps at 390 (RoleStrip's own `flex-wrap`), at the drawn
            6px gap, "Keeper 1" always in full — never clipped to "Kee". */}
        <RoleStrip
          data-testid="squad-role-strip"
          options={roleOptions}
          value={roleFilter}
          onChange={setRoleFilter}
          gap={6}
          tapBox
        />
        {/* S10 — the "Roles come from the profile's playing-role field. …"
            paragraph is the drawing talking to its own reader (the D162
            test); not built, per D168. */}
      </div>

      <SquadAverageCard average={average} onRetry={() => setReloadKey((k) => k + 1)} />

      <div data-testid="player-list" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {visibleGroups.map((group) =>
          group.players.length === 0 ? null : (
            <SquadGroup
              key={group.role}
              group={group}
              summaries={summaries}
              selectedId={selectedId}
              onSelectPlayer={onSelectPlayer}
            />
          ),
        )}
      </div>

      {/* #1511 (D398): once players exist the next invite is one tap away, the same invite the
          empty portal opens (CoachPortalEmptyState's navy variant). Navy, not gold: R-003 keeps
          Team selection as the screen's one gold. Squad.dc.html draws no invite. */}
      <ActionButton
        onClick={() => nav(coachSetupInviteHref())}
        icon={<UserPlus size={20} color="#FFFFFF" aria-hidden="true" />}
        data-testid="squad-invite-btn"
        style={{ background: "var(--nw-navy)", color: "#FFFFFF" }}
      >
        Invite a player
      </ActionButton>

      {/* S1 (D168) — Pick XI is held (#745, D93; decision 6). The drawn
          label with no count, disabled rather than hidden (Martin's own
          thread `99d0f751` asked for the button to exist), navigating
          nowhere, and carrying the fixed one-sentence explanation instead
          of any "n of 11 picked"/tick/ring. `aria-disabled`, not the
          native `disabled` attribute: the control stays reachable by
          keyboard/screen reader (it just does nothing), the same posture
          `GroundsNearYouScreen.jsx`'s own `RailLetter` already takes for a
          disabled-but-present control. No `onClick` at all is what makes
          "navigates nowhere" literally true, rather than a click handler
          that has to remember to no-op.

          NO CHEVRON (PR #920 review, F4): `Squad.dc.html:186` draws a 20px
          chevron after the label, but that drawn segment is the SAME "Team
          selection — 8 of 11 picked" affordance S1 replaces wholesale with
          the fixed, countless label and the disabled state above — a
          chevron implies "this goes somewhere," which is exactly what a
          `aria-disabled`, unclickable control must not promise. `ActionButton`
          has no trailing-icon slot either way (its own `icon` prop renders
          BEFORE `children`, matching every other gold action's leading-icon
          use, never a trailing chevron). */}
      {/* #1318 (D309): with `pick-xi` on, the entry works. Team selection is a
          gold link to `/coach-portal/team`, which lands on the next game's team
          sheet. With it off, D217 keeps today's portal byte-identical: the
          disabled button and its sentence, as S1 above describes. */}
      {newUi("pick-xi") ? (
        <Link
          to="/coach-portal/team"
          data-testid="squad-team-selection"
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            minHeight: "var(--nw-size-action)",
            boxSizing: "border-box",
            padding: "0 20px",
            background: "var(--nw-gold)",
            color: "var(--nw-ink)",
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontWeight: 700,
            fontSize: 16,
            borderRadius: "var(--nw-radius-control)",
            textDecoration: "none",
          }}
        >
          Team selection
          {/* Squad.dc.html:186 draws the 20px chevron after the label. #920 dropped it because the
              control went nowhere; now that it does, it is back (D259, #1318 review F8). */}
          <ChevronRight size={20} strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0 }} />
        </Link>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <ActionButton
            data-testid="squad-team-selection"
            aria-disabled="true"
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            Team selection
          </ActionButton>
          <div className="nw-help">
            Picking a side is coming. Nothing leaves this screen — a side is not sent anywhere yet.
          </div>
        </div>
      )}
    </div>
  );
}

/** The squad average card (ruling 1/2, S9) — loading (kit skeleton, no
 * sentence), failed (LoadFailureNote, never a partial figure), the
 * em-dash "before a dismissal" state, or the real figure. */
function SquadAverageCard({ average, onRetry }) {
  const cardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 14,
    background: "var(--nw-surface)",
    border: "1px solid var(--nw-line)",
    borderRadius: "var(--nw-radius-card)",
  };

  if (!average) {
    return (
      <div style={cardStyle} data-testid="squad-average-loading">
        <div className="nw-section">Squad average</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ height: 34, width: "40%", borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ height: 12, width: "70%", borderRadius: 6, background: "var(--nw-line)" }} />
        </div>
      </div>
    );
  }

  if (average.status === "failed") {
    return (
      <div style={cardStyle}>
        <div className="nw-section">Squad average</div>
        <LoadFailureNote what="the squad average" testId="squad-average-error" onRetry={onRetry} />
      </div>
    );
  }

  if (average.status === "no-dismissals") {
    return (
      <div style={cardStyle}>
        <div className="nw-section">Squad average</div>
        <div data-testid="squad-average" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 34, letterSpacing: "-1px", lineHeight: 1, color: "var(--nw-ink)" }}>
            —
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nw-ink-muted)" }}>
            {average.totalRuns.toLocaleString("en-NZ")} runs · no dismissals yet
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div className="nw-section">Squad average</div>
      <div data-testid="squad-average" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 34, letterSpacing: "-1px", lineHeight: 1, color: "var(--nw-ink)" }}>
          {avgText(average.value)}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nw-ink-muted)" }}>runs per dismissal</div>
      </div>
      <div className="nw-help">
        Rests on {average.totalRuns.toLocaleString("en-NZ")} runs and {average.totalDismissals.toLocaleString("en-NZ")} dismissals,
        logged between the {average.playerCount} of you. The runs are a total; the figure is the runs divided by the
        dismissals, and it moves as both do.
      </div>
    </div>
  );
}

/** One role group: the header row (label + right-hand count, S4) and its
 * rows. `squad-group-batters`/`squad-group-keeper` carry the drawn ids
 * (Controls); the All-rounder and Bowler groups have none (S7). */
function SquadGroup({ group, summaries, selectedId, onSelectPlayer }) {
  const dcId =
    group.role === "Batter" ? "squad-group-batters" : group.role === "Keeper" ? "squad-group-keeper" : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div data-testid={dcId} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
          {group.groupLabel}
        </div>
        {/* S4 — once S1 drops "n of m picked", the right-hand count is the
            number of players in the group. */}
        <div className="nw-caption-strong" style={{ color: "var(--nw-navy)" }}>
          {group.players.length}
        </div>
      </div>
      {/* S6 — every player shows; no "+N more" line. */}
      {group.players.map((p) => (
        <SquadPlayerRow
          key={p.player_id}
          player={p}
          status={playerRowStatus(summaries ? summaries[p.player_id] : null)}
          isKeeper={group.role === "Keeper"}
          selected={selectedId === p.player_id}
          onSelect={() => {
            // A coach opening a player is counted once per tap; the player is never named.
            track("coach.player_open");
            onSelectPlayer(p);
          }}
        />
      ))}
    </div>
  );
}

/**
 * One player row, built on `kit/ListRow` (PR #920 review, F3 — the earlier
 * comment here claiming ListRow could not carry this row was wrong: it
 * already has `name`, `figuresLine`, a second `refusal` line and a `tag`,
 * and its `style` MERGES with the caller's own winning on any property
 * named — exactly what's needed to drop Squad's `12px 14px`/no-min-height
 * numbers in over the DEFAULT `56`/`12px 16px` ListRow ships with today).
 *
 * The two possible SECOND lines (a not-out-only reason, ruling 3; the
 * keeper's own note, S13/ruling 7) go through `refusal` together, each as
 * its own `<span>` so the keeper note keeps its drawn, distinct colour
 * (`--nw-ink-faint`) rather than inheriting `refusal`'s own `--nw-ink-muted`
 * — no kit change needed, since a caller-supplied child's own inline style
 * already wins inside that slot.
 *
 * S1 — no trailing tick/ring: Pick XI's per-row pick marks are gone, so
 * `selected` (ListRow's tick/ring prop) is never passed here — S8's
 * tap-to-select is a page-level concern, not a drawn glyph on the row.
 * S3 — no tag chip ("Opener"): the record holds no sub-role, so `tag` is
 * never passed either.
 *
 * PR #920 review, F2 — `status` (from `lib/squadAverage.js`'s
 * `playerRowStatus`) replaces a direct `playerRowFigures(summary?.data)`
 * call: "still loading" (a skeleton, no claim at all), "failed" (a short,
 * honest note), and "a real, successful zero" ("No innings yet") are three
 * different facts, and only the last of them is what that copy means.
 */
function SquadPlayerRow({ player, status, isKeeper, selected, onSelect }) {
  let primaryLine;
  const secondaryLines = [];

  if (status.status === "loading") {
    // No sentence, no figure — a plain skeleton bar, the same convention
    // the squad average card's own loading state uses.
    primaryLine = (
      <span
        aria-hidden="true"
        style={{ display: "inline-block", height: 12, width: "55%", borderRadius: 6, background: "var(--nw-line)" }}
      />
    );
  } else if (status.status === "failed") {
    primaryLine = "Couldn't load this player's figures";
  } else {
    const figures = status.figures;
    const srText = figures.strikeRate == null ? null : figures.strikeRate.toFixed(1);
    if (figures.innings === 0) {
      // The ONE place this copy is honest: a real, successful read that
      // genuinely counted zero innings — never "still loading"/"failed"
      // wearing the same words (PR #920 review, F2).
      primaryLine = "No innings yet";
    } else if (figures.dismissals > 0) {
      primaryLine = `${inningsWord(figures.innings)} · avg ${avgText(figures.average)}${srText != null ? ` · SR ${srText}` : ""}`;
    } else {
      primaryLine = `${inningsWord(figures.innings)} · ${figures.totalRuns} runs${srText != null ? ` · SR ${srText}` : ""}`;
    }
    // Ruling 3 — a not-out-only player's own reason, in the drawing's exact
    // words, derived from the counts, never the API's avg.
    if (figures.isNotOutOnly) {
      secondaryLines.push({ text: "Not out, so there is no average yet", color: "var(--nw-ink-muted)" });
    }
  }

  // S13/D168 — issue #704 records the flag now (`matches.kept_wicket`), so
  // the row no longer needs to disclaim that it can't. This squad row
  // doesn't carry per-player wicketkeeping figures at all today, so there
  // is nothing to show in its place — just nothing false left to say.

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <ListRow
        data-testid={`player-${player.player_id}`}
        name={player.full_name}
        figuresLine={primaryLine}
        refusal={
          secondaryLines.length
            ? secondaryLines.map((line, i) => (
                <span key={i} style={{ display: "block", color: line.color }}>
                  {line.text}
                </span>
              ))
            : null
        }
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        style={{
          flex: 1,
          minWidth: 0,
          padding: "12px 14px",
          minHeight: "auto",
          border: selected ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line)",
        }}
      />
      {/* Ruling 8 — `player-record-{id}` kept, a sibling of the select
          control (never nested inside it), same convention
          `CoachPortal.jsx`'s own shipped row already uses. */}
      <Link
        to={`/coach-portal/record?player=${encodeURIComponent(player.player_id)}`}
        title={`Open ${player.full_name}'s record`}
        aria-label={`Open ${player.full_name}'s record`}
        data-testid={`player-record-${player.player_id}`}
        className="shrink-0"
        style={{
          // #1210: a 44x44 tap box (D237); the 16px icon stays centred.
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 44,
          flexShrink: 0,
          padding: 0,
          color: "var(--nw-ink-faint)",
        }}
      >
        <ClipboardList size={16} />
      </Link>
    </div>
  );
}
